import {
  EXPORT_MAX_ROWS,
  MANAGER_ROLES,
  METRIC_KEYS,
  METRIC_LABELS,
  type MetricKey,
} from '@performance-crm/shared';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { StageBadge } from '@/components/leads/badges';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { apiDownload } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { fmt } from '@/lib/format';
import { useFunnelDetails } from '@/lib/queries';
import type { PipelineStage } from '@performance-crm/shared';

const LIMIT = 100;

export function FunnelDetailsPage() {
  const [params, setParams] = useSearchParams();
  const companyId = params.get('companyId') ?? undefined;
  const month = params.get('month') ?? '';
  const monthTo = params.get('monthTo') ?? undefined;
  const months = params.get('months') ?? undefined;
  const monthList = months
    ? months.split(',').filter(Boolean)
    : monthTo
      ? [month, monthTo]
      : [month];
  const metric = (params.get('metric') ?? 'CAPTACIONES') as MetricKey;
  const projectId = params.get('projectId') ?? undefined;
  const advisorId = params.get('advisorId') ?? undefined;
  const mediumCategory = params.get('mediumCategory') ?? undefined;
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isFetching, error } = useFunnelDetails({
    companyId,
    projectId,
    advisorId,
    month,
    monthTo,
    months,
    metric,
    mediumCategory,
    offset,
    limit: LIMIT,
  });
  const toast = useToast();
  const { user } = useAuth();
  // El CSV sigue la regla de «Exportar leads»: solo quien gestiona la empresa (la API también lo exige).
  const canExport = !!user?.memberships.some(
    (m) => m.companyId === companyId && MANAGER_ROLES.includes(m.role),
  );
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params);
      qs.delete('from');
      qs.set('format', 'csv');
      const { blob, headers } = await apiDownload(`/api/v1/funnel/details?${qs}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${metric.toLowerCase()}_${monthList[0]}${monthList.length > 1 ? `_${monthList[monthList.length - 1]}` : ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      const rows = Number(headers.get('x-export-rows') ?? 0);
      const total = Number(headers.get('x-export-total') ?? rows);
      toast.success(
        `${fmt.number(rows)} fila${rows === 1 ? '' : 's'} exportada${rows === 1 ? '' : 's'}`,
        headers.get('x-export-truncated') === '1'
          ? `Solo salieron las primeras ${fmt.number(EXPORT_MAX_ROWS)} de ${fmt.number(total)}: acota los meses, el proyecto o el medio para el resto.`
          : undefined,
      );
    } catch (err) {
      toast.error('No se pudo exportar', (err as Error).message);
    } finally {
      setExporting(false);
    }
  };

  // Volver al Embudo tal como estaba (`from`); los enlaces viejos sin `from` quitan solo métrica y medio.
  const from = params.get('from');
  const back = new URLSearchParams(from ?? params);
  if (from === null) {
    back.delete('metric');
    back.delete('mediumCategory');
  }

  return (
    <div className="space-y-4">
      <Link
        to={`/embudo?${back}`}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
      >
        <ArrowLeft className="h-4 w-4" /> Embudo
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Detalle: {METRIC_LABELS[metric]}
          </h1>
          <p className="text-muted-foreground text-sm">
            {months ? monthList.join(', ') : `${month}${monthTo ? ` – ${monthTo}` : ''}`}
            {mediumCategory ? ` · ${mediumCategory}` : ''} ·{' '}
            {data ? `${fmt.number(data.total)} filas` : '…'} · cada fila es un hecho que cuenta en
            la métrica
          </p>
        </div>
        <div className="flex gap-2">
          <Select
            value={metric}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              next.set('metric', e.target.value);
              setOffset(0);
              setParams(next, { replace: true });
            }}
            aria-label="Métrica"
            className="w-56"
          >
            {METRIC_KEYS.map((k) => (
              <option key={k} value={k}>
                {METRIC_LABELS[k]}
              </option>
            ))}
          </Select>
          {canExport && (
            <Button variant="outline" onClick={exportCsv} disabled={exporting || !data}>
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}{' '}
              Exportar CSV
            </Button>
          )}
        </div>
      </div>
      {error && (
        <p role="alert" className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
          {(error as Error).message}
        </p>
      )}
      <div className="bg-card overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground text-left text-xs uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Contacto</th>
              <th className="hidden px-3 py-2 md:table-cell">Proyecto</th>
              <th className="hidden px-3 py-2 md:table-cell">Asesor</th>
              <th className="hidden px-3 py-2 lg:table-cell">Medio</th>
              <th className="px-3 py-2">Etapa</th>
              <th className="hidden px-3 py-2 lg:table-cell">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              [0, 1, 2, 3].map((i) => (
                <tr key={i} className="border-t">
                  <td colSpan={7} className="px-3 py-3">
                    <Skeleton className="h-5 w-full" />
                  </td>
                </tr>
              ))}
            {data?.rows.map((r) => (
              <tr key={`${r.entityId}-${r.metric}`} className="border-t" data-testid="detail-row">
                <td className="whitespace-nowrap px-3 py-2">{fmt.dateTime(r.date)}</td>
                <td className="px-3 py-2">
                  <Link to={`/leads/${r.leadId}`} className="font-medium hover:underline">
                    {r.contactName}
                  </Link>
                  <p className="text-muted-foreground text-xs">{r.phone ?? ''}</p>
                </td>
                <td className="hidden px-3 py-2 md:table-cell">{r.projectName}</td>
                <td className="hidden px-3 py-2 md:table-cell">{r.advisorName ?? '—'}</td>
                <td className="hidden px-3 py-2 lg:table-cell">
                  {r.medium ?? '—'}{' '}
                  <span className="text-muted-foreground text-xs">{r.mediumCategory ?? ''}</span>
                </td>
                <td className="px-3 py-2">
                  <StageBadge stage={r.pipelineStage as PipelineStage} />
                </td>
                <td
                  className="hidden max-w-xs truncate px-3 py-2 lg:table-cell"
                  title={r.detail ?? ''}
                >
                  {r.detail ?? '—'}
                </td>
              </tr>
            ))}
            {data && data.rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-muted-foreground px-3 py-8 text-center">
                  Sin hechos para esta métrica en el mes.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {data && data.total > LIMIT && (
        <div className="text-muted-foreground flex items-center justify-between text-sm">
          <span>
            {offset + 1}–{Math.min(offset + LIMIT, data.total)} de {fmt.number(data.total)}
            {isFetching && ' · actualizando…'}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + LIMIT >= data.total}
              onClick={() => setOffset(offset + LIMIT)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
